import type { AdyenCheckoutError, ResultCode } from '@adyen/adyen-web';
import { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';

import {
  isCancelError,
  makeAddressInputFromAppleContact,
  makeApplePayErrorsFromAddressErrors,
} from '@/components/ApplePayExpressButton/helper';
import type {
  OnAuthorizedHandler,
  OnShippingContactSelectedHandler,
} from '@/components/ApplePayExpressButton/types';
import { APOLLO_CLIENTS } from '@/graphql/client';
import {
  type OrderAppError,
  useOrderAbandonMutation,
  useOrderCreateMutation,
  useUpdateMetadataMutation,
} from '@/graphql/orderApp/api';
import { useBillingCountry } from '@/hooks/useBillingCountries';
import { useCheckout } from '@/hooks/useCheckout';
import { useRouter } from '@/hooks/useRouter';
import { useTransactionInitialize } from '@/hooks/useTransactionInitialize';
import { useUser } from '@/hooks/useUser';
import { captureError } from '@/lib/errors';
import { genericErrorHandler } from '@/lib/forms/helpers';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { handleTransactionResponse } from '@/lib/payment/helpers';
import type { AdyenCheckoutOpts, TransactionAction } from '@/lib/payment/types';
import { PAYMENT_TYPES } from '@/lib/payment/types';
import type { ApiError, ApiErrorType } from '@/lib/types';
import { commonMessages } from '@/messages/common';
import { useProcessingContext } from '@/providers/ProcessingProvider/ProcessingProviderContext';
import { useRegionProvider } from '@/providers/RegionProvider';
import { checkoutActions, checkoutState } from '@/states/checkout';

interface UseApplePayExpressPaymentFlowProps {
  affiliateOptInRef: React.MutableRefObject<boolean>;
  errorHandler?: (
    errors: ApiError[],
    opts?: { errorType?: ApiErrorType },
  ) => void;
  initializeApplePay: () => Promise<void>;
}
export const useApplePayExpressPaymentFlow = ({
  affiliateOptInRef,
  errorHandler,
  initializeApplePay,
}: UseApplePayExpressPaymentFlowProps) => {
  const { formatMessage } = useIntl();
  const { paths, push } = useRouter();
  const { country, locale } = useRegionProvider();
  const { setIsProcessing } = useProcessingContext();
  const {
    snapshot: { isAuthenticated, user },
  } = useUser();
  const {
    addressUpdate,
    emailUpdate,
    endProcessing,
    snapshot: {
      checkout: { id },
      checkout,
      errors,
      isChangedAfterOrderCreated,
      orderId,
      preOrderData,
    },
    startProcessing,
  } = useCheckout<true>();
  const { billingCountries } = useBillingCountry();
  const orderRef = useRef<string | undefined>(orderId || '');
  const transactionRef = useRef<string | undefined>(undefined);
  const isDepositTransaction = !!preOrderData?.deposit;
  const transactionInitialize = useTransactionInitialize(id);
  const isChangedAfterOrderCreatedRef = useRef<boolean>(
    isChangedAfterOrderCreated,
  );
  const [orderCreate] = useOrderCreateMutation({
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });
  const [orderAbandon] = useOrderAbandonMutation({
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });
  const [updateMetadata] = useUpdateMetadataMutation();

  const getTransactionAmount = (): number => {
    let amount;
    if (!isDepositTransaction) {
      amount = checkoutState.checkout?.totalPrice?.gross.amount;
    } else {
      amount = checkoutState.preOrderData?.deposit;
    }

    return amount ?? 0;
  };

  const errorHandlerApplePayExpress =
    errorHandler ??
    genericErrorHandler({
      formatMessage,
      apiErrorsHandler: checkoutActions.setErrors,
      errorType: 'adyen',
    });

  const handleShippingAddressChange: OnShippingContactSelectedHandler =
    useCallback(async (resolve, reject, event) => {
      setIsProcessing(true);
      const currentTotal = {
        label: formatMessage(commonMessages.total),
        amount: `${getTransactionAmount()}`,
        type: 'pending',
      } as ApplePayJS.ApplePayLineItem;
      try {
        const shippingContact = event?.shippingContact;

        if (!shippingContact) {
          resolve({
            newTotal: currentTotal,
            errors: [new ApplePayError('shippingContactInvalid')],
          });
          return;
        } else if (shippingContact.countryCode !== country.code) {
          resolve({
            newTotal: currentTotal,
            errors: [new ApplePayError('addressUnserviceable')],
          });
          return;
        }

        // Only a partial shipping address is available here.
        // User this partial address (with placeholder on address detail) to trigger backend recalculation of the order amount
        const shippingAddress = makeAddressInputFromAppleContact(
          shippingContact,
          undefined,
          true,
        );
        const addressErrors = await addressUpdate('shipping', shippingAddress);

        if (Array.isArray(addressErrors) && addressErrors.length) {
          const errors = makeApplePayErrorsFromAddressErrors(
            addressErrors as OrderAppError[],
            locale?.slug,
          );
          resolve({
            newTotal: currentTotal,
            errors,
          });
          return;
        }

        if (orderRef.current) {
          fetch(paths.api.express.updateOrder.asPath(), {
            method: 'POST',
            body: JSON.stringify({
              id: orderRef.current,
              shippingAddress: shippingAddress,
            }),
          });
        }

        // Occasionally the shopping bag is cleared unexpectedly - likely a bug to investigate
        if (getTransactionAmount() <= 0) {
          reject();
        }
        const newTotal = {
          label: formatMessage(commonMessages.total),
          amount: `${getTransactionAmount()}`,
          type: 'final',
        } as ApplePayJS.ApplePayLineItem;
        resolve({
          newTotal,
        });
      } catch (e) {
        console.error('[ApplePay] Failed to update shipping address:', e);
        resolve({
          newTotal: currentTotal,
          errors: [
            {
              code: 'shippingContactInvalid',
              message: 'Error occurred, please try again later.',
            },
          ],
        });
      }
    }, []);

  const handleSubmit = useCallback(
    (gatewayId?: string): AdyenCheckoutOpts['onSubmit'] =>
      async (state, component, actions) => {
        startProcessing();
        try {
          const type = isDepositTransaction ? 'DEPOSIT' : 'FULL';

          if (
            !orderRef.current ||
            (orderRef.current && isChangedAfterOrderCreatedRef.current)
          ) {
            if (isChangedAfterOrderCreatedRef.current && orderRef.current) {
              const res = await orderAbandon({
                variables: { checkoutId: id, orderId: orderRef.current ?? '' },
              });
              if (res.errors?.length) {
                errorHandlerApplePayExpress(errors, { errorType: 'orderApp' });
                endProcessing();
                return actions.reject();
              }
            }

            const createOrder = async (checkoutId: string) => {
              checkoutActions.setOrderId('');
              const { data } = await orderCreate({
                variables: { id: checkoutId },
              });

              const errors = data?.orderCreate?.errors;
              if (errors?.length) {
                errorHandlerApplePayExpress(errors, { errorType: 'orderApp' });
                return false;
              }
              orderRef.current = data?.orderCreate?.order?.id;
              orderRef.current && checkoutActions.setOrderId(orderRef.current);
              isChangedAfterOrderCreatedRef.current = false;
              checkoutActions.setIsChangedAfterOrderCreated(false);

              return !!orderRef.current;
            };

            const success = await createOrder(checkout.id);
            if (!success) return actions.reject();
          }

          if (!orderRef.current) return actions.reject();

          const { data } = await transactionInitialize({
            id: orderRef.current,
            action: 'AUTHORIZE' as TransactionAction,
            amount: getTransactionAmount(),
            data: {
              ...state.data,
            },
            gatewayId,
            type,
          });

          transactionRef.current = data?.transactionInitialize?.transaction?.id;
          const resultCode = data?.transactionInitialize?.data?.resultCode;
          const confirmationUrl = paths.store.order.confirmation.url({
            id: orderRef.current,
          });
          const _params = {
            amountRemainingToBePaid: true,
            push,
            errorHandler: errorHandlerApplePayExpress,
            paymentType: PAYMENT_TYPES.APPLE_PAY_EXPRESS,
            redirectPaths: {
              Authorised: confirmationUrl,
            },
            onAuthorize: () => {
              if (user?.id && isAuthenticated && affiliateOptInRef.current) {
                updateMetadata({
                  variables: {
                    id: user.id,
                    input: [
                      {
                        key: ACCOUNT_METADATA.isAffiliated,
                        value: 'true',
                      },
                    ],
                  },
                  context: {
                    client: APOLLO_CLIENTS.orderApp,
                  },
                });
              }
              return actions.resolve({
                resultCode: resultCode as ResultCode,
                action: undefined,
                order: undefined,
                donationToken: undefined,
                error: undefined,
              });
            },
          };
          const result = await handleTransactionResponse(
            data?.transactionInitialize?.data,
            _params,
          );
          if (!result) {
            actions.resolve({
              resultCode: 'Error',
              action: undefined,
              order: undefined,
              donationToken: undefined,
              error: undefined,
            });
            component.unmount();
            initializeApplePay();
          }
        } catch (err) {
          actions.reject();
          component.unmount();
          initializeApplePay();
          throw err;
        } finally {
          setIsProcessing(false);
          endProcessing();
        }
      },
    [transactionInitialize, orderCreate, orderAbandon, errorHandler, id],
  );

  const handleError = useCallback(
    async (error: AdyenCheckoutError) => {
      setIsProcessing(false);

      if (!isCancelError(error)) {
        captureError(error, {
          extra: {
            error,
            checkoutId: id,
          },
        });
        errorHandlerApplePayExpress([{ code: 'PAYMENT_ERROR' }]);
      }
    },
    [setIsProcessing, id],
  );

  const handleAuthorized: OnAuthorizedHandler = useCallback(
    async ({ authorizedEvent }, actions) => {
      // At this point, we have access to the full shipping address.
      // Must re-validate and update the address again with backend.
      const shippingContact = authorizedEvent.payment.shippingContact;
      const billingContact = authorizedEvent.payment.billingContact;
      if (!shippingContact || !billingContact) {
        actions.reject();
        return;
      }

      if (
        billingCountries.length > 0 &&
        !(
          billingContact.countryCode &&
          billingCountries.some(
            (country) => billingContact.countryCode == country.code,
          )
        )
      ) {
        actions.reject({
          code: 'billingContactInvalid',
          contactField: 'countryCode',
          message: 'Invalid billing country',
        });
        return;
      }
      const shippingAddress = makeAddressInputFromAppleContact(
        shippingContact,
        user?.addresses ?? [],
        false,
      );
      const billingAddress = makeAddressInputFromAppleContact(
        billingContact,
        undefined,
        false,
        shippingContact.phoneNumber, // If no phone number available for billing address in Apple Pay payment sheet, use the shipping phone number instead
      );
      const addressErrors = await addressUpdate('shipping', shippingAddress);

      if (Array.isArray(addressErrors) && addressErrors.length) {
        const errors = makeApplePayErrorsFromAddressErrors(
          addressErrors as OrderAppError[],
          locale?.slug,
        );
        actions.reject(errors[0]);
        return;
      }

      await fetch(paths.api.express.updateCheckoutBillingAddress.asPath(), {
        method: 'POST',
        body: JSON.stringify({
          id: checkout.id,
          address: billingAddress,
        }),
      });

      const userEmail =
        user?.email ?? authorizedEvent.payment.shippingContact?.emailAddress;
      if (userEmail) {
        emailUpdate(userEmail);
      }

      if (orderRef.current) {
        await fetch(paths.api.express.updateOrder.asPath(), {
          method: 'POST',
          body: JSON.stringify({
            id: orderRef.current,
            shippingAddress,
            billingAddress,
            userEmail,
          }),
        });
      }

      return actions.resolve();
    },
    [],
  );

  return {
    handleSubmit,
    handleError,
    handleShippingAddressChange,
    handleAuthorized,
  };
};
